import { Avatar, Button, Group, Menu, rem, Text } from '@mantine/core';
import type { User } from '@supabase/supabase-js';
import { IconLogout } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { getSupabaseBrowserClient } from '@/lib/supabase/browser';

/**
 * User menu for the Vite SPA.
 *
 * Mirrors src/components/auth/{UserMenu,AuthenticatedUserMenu}.tsx from the
 * Next.js build but uses react-router's useNavigate instead of
 * next/navigation's useRouter. Rendered by SplitLayout in the
 * workspace top-right on every authenticated route — kept for parity with the old app
 * and for the Playwright E2E fixtures that wait on `[data-testid="user-menu"]`.
 */
export function AuthenticatedUserMenu() {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const supabase = getSupabaseBrowserClient();
    let cancelled = false;

    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUser(data.user ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!cancelled) setUser(session?.user ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  if (!user) return null;

  // Positioning is the caller's job (SplitLayout places this in the
  // workspace's top-right corner).
  return <UserMenu user={user} />;
}

function UserMenu({ user }: Readonly<{ user: User }>) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    setLoading(true);
    try {
      const supabase = getSupabaseBrowserClient();
      await supabase.auth.signOut();
    } catch {
      // Sign-out failed (network error, etc.) — navigate to login anyway
      // so the user isn't stuck on a broken session.
    }
    navigate('/auth/login', { replace: true });
  };

  const displayName = user.user_metadata?.full_name || user.email || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Button variant="subtle" color="navy" p="xs" data-testid="user-menu">
          <Group gap="xs">
            {/* Dark-on-light: this menu sits on the ivory workspace. */}
            <Avatar size="sm" radius="xl" variant="filled" color="navy.8">
              {initials}
            </Avatar>
            <Text
              size="sm"
              maw={150}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {displayName}
            </Text>
          </Group>
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          color="red"
          leftSection={
            <IconLogout style={{ width: rem(14), height: rem(14) }} />
          }
          onClick={handleLogout}
          disabled={loading}
        >
          Logout
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
